import React, { useState } from "react";
import {
  AlertTriangle,
  Baby,
  Crown,
  Lock,
  Pencil,
  RefreshCw,
  Sparkles,
  Unlock,
  Users,
} from "lucide-react";
import { SeatRing } from "@/components/SeatRing";
import { GuestChip } from "@/components/GuestChip";
import { dietShort, domainLabel } from "@/lib/format";
import { t, tf } from "@/i18n";

export const TableCard = ({
  table,
  validation,
  guests,
  conflictIds,
  selectedGuestId,
  draggingGuest,
  blockedBy,
  onDropGuest,
  onSelectGuest,
  onRequestMove,
  onToggleLock,
  onRename,
  isHeadTable,
  isChildrenTable,
  pickedIds,
  onDragStart,
  onDragEnd,
}) => {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(table.title || "");

  const commitTitle = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (table.title || "")) onRename?.(table.table, next);
  };
  const conflicts = validation?.conflicts || [];
  const overCapacity = validation?.over_capacity;
  const blocked = Boolean(draggingGuest && blockedBy?.length);
  const dropState = hovered ? (blocked ? "dropzone-blocked" : "dropzone-active") : "";
  const defaultTitle = `${t("overview.table")} ${table.table}`;

  return (
    <div
      data-testid={`table-card-${table.table}`}
      className={`surface flex flex-col rounded-xl p-4 transition-shadow duration-200 ${dropState}`}
      style={{ borderColor: conflicts.length || overCapacity ? "var(--danger)" : undefined }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <span className="font-num text-[10px] font-semibold tracking-[0.18em] text-[color:var(--gild)]">
            {tf("table.label", { n: table.table })}
          </span>
          <button
            type="button"
            onClick={() => onToggleLock?.(table.table, !table.locked)}
            data-testid={`table-lock-toggle-${table.table}`}
            aria-pressed={Boolean(table.locked)}
            title={table.locked ? t("table.lockedTitle") : t("table.lockTitle")}
            className="rounded-full p-1 transition-colors hover:bg-[color:var(--paper-2)]"
            style={{ color: table.locked ? "var(--gild)" : "var(--muted-ink)" }}
          >
            {table.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          </button>
        </span>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {isChildrenTable ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#f2f6f3", color: "var(--sage-dark)" }}
              data-testid={`table-children-badge-${table.table}`}
            >
              <Baby className="h-3 w-3" /> {t("table.children")}
            </span>
          ) : null}
          {isHeadTable ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#f7f1e6", color: "var(--gild)" }}
              data-testid={`table-head-badge-${table.table}`}
            >
              <Crown className="h-3 w-3" /> {t("table.head")}
            </span>
          ) : null}
          {table.locked ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#f7f1e6", color: "var(--gild)" }}
              data-testid={`table-locked-badge-${table.table}`}
            >
              <Lock className="h-3 w-3" /> {t("table.locked")}
            </span>
          ) : null}
          {overCapacity ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#fdf1f0", color: "var(--danger)" }}
              data-testid={`table-over-capacity-${table.table}`}
            >
              <Users className="h-3 w-3" /> {t("table.over")}
            </span>
          ) : null}
          {conflicts.length ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#fdf1f0", color: "var(--danger)" }}
              data-testid={`table-conflict-badge-${table.table}`}
            >
              <AlertTriangle className="h-3 w-3" /> {tf("table.conflicts", { n: conflicts.length })}
            </span>
          ) : null}
          {table.justification_stale ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: "#fdf8ef", color: "#9a7a3f" }}
              data-testid={`table-note-stale-${table.table}`}
            >
              <RefreshCw className="h-3 w-3" /> {t("table.staleNote")}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-2 flex items-start gap-3">
        <div className="shrink-0">
          <SeatRing
            capacity={table.capacity}
            used={table.seats_used}
            conflict={Boolean(conflicts.length || overCapacity)}
            size={104}
          />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commitTitle}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitTitle();
                if (event.key === "Escape") setEditing(false);
              }}
              maxLength={60}
              placeholder={defaultTitle}
              className="font-display w-full rounded border bg-white px-2 py-1 text-[19px] font-semibold leading-tight outline-none"
              style={{ borderColor: "var(--gild)" }}
              data-testid={`table-title-input-${table.table}`}
            />
          ) : (
            <h3
              className="font-display group/title flex cursor-text items-center gap-1.5 text-[19px] font-semibold leading-tight"
              onClick={() => {
                if (!onRename) return;
                setDraft(table.title || "");
                setEditing(true);
              }}
              title={onRename ? t("table.rename") : undefined}
              data-testid={`table-title-${table.table}`}
            >
              <span className="truncate">{table.title || defaultTitle}</span>
              {onRename ? (
                <Pencil className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/title:opacity-60" />
              ) : null}
            </h3>
          )}
          <p
            className="clamp-3 mt-1 text-[12.5px] italic leading-snug text-[color:var(--ink-soft)]"
            title={table.justification}
            data-testid={`table-justification-${table.table}`}
          >
            {table.justification}
          </p>
          {(table.top_domains || []).length ? (
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`table-domains-${table.table}`}>
              {table.top_domains.slice(0, 3).map((domain) => (
                <span
                  key={domain}
                  className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: "#f2f6f3", color: "var(--sage-dark)" }}
                >
                  {domainLabel(domain)}
                </span>
              ))}
            </div>
          ) : null}
          {table.ai_written ? (
            <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--gild)]">
              <Sparkles className="h-3 w-3" /> {t("table.aiNote")}
            </span>
          ) : null}
        </div>
      </div>

      <div
        data-testid={`table-dropzone-${table.table}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!hovered) setHovered(true);
        }}
        onDragEnter={() => setHovered(true)}
        onDragLeave={() => setHovered(false)}
        onDrop={(event) => {
          event.preventDefault();
          setHovered(false);
          const guestId = event.dataTransfer.getData("text/plain");
          if (guestId) onDropGuest?.(guestId, table.table, blockedBy || []);
        }}
        className="mt-3 min-h-[52px] flex-1 rounded-lg border border-dashed p-2"
        style={{
          borderColor: hovered ? (blocked ? "var(--danger)" : "var(--gild)") : "rgba(230,222,211,0.9)",
          backgroundColor: "var(--paper)",
        }}
      >
        <div className="flex flex-wrap gap-1.5">
          {guests.map((guest) => (
            <GuestChip
              key={guest.id}
              guest={guest}
              conflict={conflictIds.has(guest.id)}
              selected={pickedIds ? pickedIds.has(guest.id) : selectedGuestId === guest.id}
              onSelect={onSelectGuest}
              onRequestMove={onRequestMove}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dragging={draggingGuest?.id === guest.id}
            />
          ))}
          {!guests.length ? (
            <p className="px-1 py-2 text-xs text-[color:var(--muted-ink)]">{t("table.empty")}</p>
          ) : null}
        </div>
      </div>

      {Object.keys(table.diet_summary || {}).length ? (
        <p className="mt-2 text-[11px] text-[color:var(--muted-ink)]">
          {t("table.kitchen")}{" "}
          {Object.entries(table.diet_summary)
            .map(([tag, count]) => `${count} ${dietShort(tag)}`)
            .join(" · ")}
        </p>
      ) : null}
    </div>
  );
};

export default TableCard;
